import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { FC, useEffect, useState, useMemo } from "react";
import DateTimePicker from "shared/components/Form/DateTimePicker";
import SecondarySwitch from "shared/components/Form/SecondarySwitch";
import { COLOR_BLACK_60, COLOR_PRIMARY_MAIN } from "shared/theme/colors";
import { StyledContainer } from "./styled";

const DEFAULT_TIMEZONE = "";

const getMinEndTime = (startTime: string | null): string | null => {
  if (!startTime) return null;

  const [hourStr, minuteStr] = startTime.split(":");
  const hour = parseInt(hourStr);
  const minute = parseInt(minuteStr);
  // Add 15 minutes
  let newMinute = minute + 15;
  let newHour = hour + Math.floor(newMinute / 60);
  newMinute = newMinute % 60;

  return `${newHour.toString().padStart(2, "0")}:${newMinute.toString().padStart(2, "0")}`;
};

export type SchedulingPickerProps = {
  scheduledStartAt: string;
  onScheduledStartAtChange: (scheduledStartAt: string) => void;
  scheduledEndAt?: string;
  onScheduledEndAtChange?: (scheduledEndAt: string) => void;
  isSchedulingEnabled: boolean;
  onSchedulingEnabledChange: (isEnabled: boolean) => void;
  disabled?: boolean;
  title?: string;
  defaultTimezone?: string;
  formatScheduledDateTime?: (
    date: Date,
    time: string,
    timezone: string,
  ) => string;
  minDate?: Date;
  showToggle?: boolean;
  showEndDate?: boolean;
  dateLabel?: string;
  endDateLabel?: string;
  toggleTrackColor?: string;
};

const SchedulingPicker: FC<SchedulingPickerProps> = ({
  scheduledStartAt,
  onScheduledStartAtChange,
  scheduledEndAt = "",
  onScheduledEndAtChange,
  isSchedulingEnabled,
  onSchedulingEnabledChange,
  disabled = false,
  title = "Scheduling",
  defaultTimezone = DEFAULT_TIMEZONE,
  formatScheduledDateTime,
  minDate,
  showToggle = true,
  showEndDate = false,
  dateLabel = "Start Date",
  endDateLabel = "End Date",
  toggleTrackColor = COLOR_PRIMARY_MAIN,
}) => {
  const [date, setDate] = useState<Date | null>(null);
  const [time, setTime] = useState<string>("");
  const [timezone, setTimezone] = useState(defaultTimezone);

  const [endDate, setEndDate] = useState<Date | null>(null);
  const [endTime, setEndTime] = useState<string>("");
  const [endTimezone, setEndTimezone] = useState(defaultTimezone);

  const isStartDateComplete = useMemo(
    () => !!(date && time && timezone),
    [date, time, timezone],
  );

  const minEndTime = useMemo(() => getMinEndTime(time), [time]);

  useEffect(() => {
    if (scheduledStartAt) {
      const [dateStr, timeStr, tz] = scheduledStartAt.split(" ");

      if (!isSchedulingEnabled) {
        onSchedulingEnabledChange(true);
      }

      setDate(new Date(dateStr));
      setTime(timeStr);
      setTimezone(tz || defaultTimezone);
    }
  }, [
    scheduledStartAt,
    isSchedulingEnabled,
    onSchedulingEnabledChange,
    defaultTimezone,
  ]);

  useEffect(() => {
    if (scheduledEndAt && showEndDate && onScheduledEndAtChange) {
      const [dateStr, timeStr, tz] = scheduledEndAt.split(" ");

      setEndDate(new Date(dateStr));
      setEndTime(timeStr);
      setEndTimezone(tz || defaultTimezone);
    }
  }, [scheduledEndAt, showEndDate, onScheduledEndAtChange, defaultTimezone]);

  useEffect(() => {
    if (timezone && isStartDateComplete) {
      setEndTimezone(timezone);
    }
  }, [timezone, isStartDateComplete]);

  useEffect(() => {
    if (
      endDate &&
      date &&
      endDate.toDateString() === date.toDateString() &&
      endTime &&
      minEndTime &&
      endTime < minEndTime
    ) {
      setEndTime("");
    }
  }, [date, endDate, time, endTime, minEndTime]);

  const handleToggleChange = (value: boolean) => {
    onSchedulingEnabledChange(value);
    if (!value) {
      setDate(null);
      setTime("");
      setTimezone(defaultTimezone);
      onScheduledStartAtChange("");

      if (showEndDate && onScheduledEndAtChange) {
        setEndDate(null);
        setEndTime("");
        setEndTimezone(defaultTimezone);
        onScheduledEndAtChange("");
      }
    }
  };

  const handleStartTimezoneChange = (tz: string) => {
    setTimezone(tz);
    if (isStartDateComplete) {
      setEndTimezone(tz);
    }
  };

  const handleStartDateChange = (newStartDate: Date | null) => {
    setDate(newStartDate);

    if (newStartDate && endDate) {
      const startDateOnly = new Date(newStartDate);
      startDateOnly.setHours(0, 0, 0, 0);

      const endDateOnly = new Date(endDate);
      endDateOnly.setHours(0, 0, 0, 0);

      if (
        startDateOnly > endDateOnly ||
        (startDateOnly.getTime() === endDateOnly.getTime() &&
          time &&
          endTime &&
          time >= endTime)
      ) {
        setEndDate(null);
        setEndTime("");
        if (onScheduledEndAtChange) {
          onScheduledEndAtChange("");
        }
      }
    }
  };

  const handleStartTimeChange = (newStartTime: string) => {
    setTime(newStartTime);

    if (
      endDate &&
      date &&
      endDate.toDateString() === date.toDateString() &&
      endTime &&
      newStartTime >= endTime
    ) {
      setEndTime("");
      if (endDate && endTimezone && onScheduledEndAtChange) {
        const formattedEndDate = formatScheduledAt(endDate, "", endTimezone);
        onScheduledEndAtChange(formattedEndDate);
      }
    }
  };

  const formatScheduledAt =
    formatScheduledDateTime ||
    ((
      currentDate: Date,
      currentTime: string,
      currentTimezone: string,
    ): string => {
      const year = currentDate.getFullYear();
      const month = String(currentDate.getMonth() + 1).padStart(2, "0");
      const day = String(currentDate.getDate()).padStart(2, "0");
      const dateStr = `${year}-${month}-${day}`;

      return `${dateStr} ${currentTime} ${currentTimezone}`;
    });

  return (
    <StyledContainer>
      {showToggle && (
        <Box display="flex" alignItems="center" columnGap="10px">
          <Typography
            variant="bodyMSemiBold"
            color={COLOR_BLACK_60}
            letterSpacing="1.4px"
            textTransform="uppercase"
          >
            {title}
          </Typography>
          <SecondarySwitch
            value={isSchedulingEnabled}
            onChange={handleToggleChange}
            trackColor={toggleTrackColor}
            disabled={disabled}
          />
        </Box>
      )}

      {isSchedulingEnabled && (
        <Box display="flex" flexDirection="column" mt={2} gap={2}>
          <DateTimePicker
            date={date}
            time={time}
            timezone={timezone}
            onDateChange={handleStartDateChange}
            onTimeChange={handleStartTimeChange}
            onTimezoneChange={handleStartTimezoneChange}
            onCombinedDateTimeChange={onScheduledStartAtChange}
            formatCombinedDateTime={formatScheduledAt}
            disabled={disabled}
            minDate={minDate}
            maxDaysInFuture={365}
            dateLabel={dateLabel}
          />

          {showEndDate && onScheduledEndAtChange && (
            <DateTimePicker
              date={endDate}
              time={endTime}
              timezone={endTimezone}
              onDateChange={setEndDate}
              onTimeChange={setEndTime}
              onTimezoneChange={setEndTimezone}
              onCombinedDateTimeChange={onScheduledEndAtChange}
              formatCombinedDateTime={formatScheduledAt}
              disabled={disabled || !isStartDateComplete}
              minDate={date || minDate} // End date must be after start date
              maxDaysInFuture={365}
              minTime={
                endDate &&
                date &&
                endDate.toDateString() === date.toDateString()
                  ? minEndTime
                  : undefined
              }
              dateLabel={endDateLabel}
            />
          )}
        </Box>
      )}
    </StyledContainer>
  );
};

export default SchedulingPicker;
